import { useCallback, useRef, useState } from 'react';

import { message } from 'antd';
import { useIntl } from 'react-intl';

import type { FilterContainerActionButton } from '@src/components/FilterContainer/FilterContainer.interfaces';
import type { ReportPdfSelection } from '@src/models/interfaces/reportPdf.interface';
import { ReportPdfService } from '@src/services/ReportPdfService';

export const useServerReportPdf = (selection?: ReportPdfSelection): FilterContainerActionButton & { action: () => Promise<void> } => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const download = useCallback(async () => {
    if (!selection?.organizationId || inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const locale: 'es' | 'en' = new Intl.Locale(intl.locale).language === 'en' ? 'en' : 'es';
      const normalized = selection.reportType === 'operations'
        ? { ...selection, variant: 'summary' as const, locale }
        : { ...selection, variant: 'full' as const, locale };
      const result = await ReportPdfService.generate(normalized);
      // Storage supplies Content-Disposition, including the server-generated filename.
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = result.fileName;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading server report PDF:', error);
      message.error(intl.formatMessage({ id: 'dashboards.reportPdf.server.error' }));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [intl, selection]);

  return {
    label: intl.formatMessage({ id: 'dashboards.reportPdf.server.action' }),
    iconName: 'picture_as_pdf',
    type: 'secondary',
    action: download,
    loading,
    disabled: loading || !selection?.organizationId,
  };
};
